<template>
  <div class="single-property-component-item">
    <div v-if="!disabled" class="flex my-2">
      <div class="flex flex-1 justify-end">
        <a>
          <MTooltip :class="{ invisible: !canEdit && !disabled }">
            <template v-slot:trigger>
              <MIcon
                :id="`edit-${__tc(item.type)}-property-${item.id}`"
                name="pencil"
                class="mx-2 cursor-pointer text-primary"
                @click="$emit('edit', item)"
              />
            </template>
            {{ $t('edit') }}
          </MTooltip>
        </a>
        <FlotoDeleteBtn
          :id="`delete-${__tc(item.type)}-property-${item.id}`"
          :class="{ invisible: !canDelete && !disabled }"
          :message="$t('confirm_delete_item', { item: __tc(type) })"
          @confirm="$emit('remove', item)"
        />
      </div>
    </div>
    <MRow :gutter="0">
      <MCol :size="12">
        <MRow :gutter="0" class="flex-1">
          <MCol
            v-for="field in fieldDetails.fields"
            :key="field.key"
            :size="colSize"
          >
            <MRow :gutter="0">
              <MCol class="mx-2 min-w-0">
                <FlotoFormItemReadOnly :label="field.name" :rules="field.rules">
                  <div class="text-ellipsis">
                    <ProductPicker
                      v-if="field.inputType === 'product'"
                      :value="item[field.paramName]"
                      placeholder="---"
                      as-input
                      text-only
                      disabled
                    />
                    <VendorPicker
                      v-if="field.inputType === 'vendor'"
                      :value="item[field.paramName]"
                      placeholder="---"
                      as-input
                      text-only
                      disabled
                    />
                    <ManufacturerPicker
                      v-else-if="field.inputType === 'manufacturer'"
                      :key="item[field.paramName]"
                      :value="item[field.paramName]"
                      placeholder="---"
                      as-input
                      text-only
                      disabled
                    />
                    <SoftwareLicenseTypePicker
                      v-else-if="field.inputType === 'softwareLicenseType'"
                      :value="item[field.paramName]"
                      placeholder="---"
                      as-input
                      text-only
                      disabled
                    />
                    <AssetPickerDrawer
                      v-else-if="field.inputType === 'hardwareAssetPicker'"
                      :value="item[field.paramName]"
                      :module-name="moduleName"
                      clickable
                      disabled
                      show-only-name
                      placeholder="---"
                    />
                    <template v-else>
                      <span
                        :title="item[field.paramName] ? getValue(field) : ''"
                      >
                        {{ getValue(field) }}
                        <span v-if="item[field.currency]">
                          <CurrencyPicker
                            text-only
                            :value="item[field.currency]"
                            disabled
                            style="white-space: nowrap"
                          />
                        </span>
                      </span>
                    </template>
                  </div>
                </FlotoFormItemReadOnly>
              </MCol>
            </MRow>
          </MCol>
        </MRow>
      </MCol>
      <!-- For custom field rendering -->
      <MCol v-if="customFields.length" :size="12">
        <MRow :gutter="0" class="flex-1">
          <MCol v-for="f in customFields" :key="f.key" :size="colSize">
            <MRow :gutter="0">
              <MCol class="mx-2">
                <FlotoFormItemReadOnly :label="f.title">
                  <!-- only for attachment  -->
                  <template v-if="f.inputType === 'upload'">
                    <FlotoAttachment
                      v-if="((item.fieldValueDetails || {})[f.id] || []).length"
                      :key="((item.fieldValueDetails || {})[f.id] || []).length"
                      disabled
                      :value="(item.fieldValueDetails || {})[f.id]"
                    />
                    <span v-else>---</span>
                  </template>
                  <template
                    v-else-if="
                      ['dropdown', 'dependent', 'multiSelectDropdown'].indexOf(
                        f.inputType
                      ) >= 0 && f.fieldResolutionType === 'system_field'
                    "
                  >
                    <CustomAsSystemPicker
                      text-only
                      disabled
                      :as-tag="f.inputType === 'multiSelectDropdown'"
                      :multiple="f.inputType === 'multiSelectDropdown'"
                      :as-input="false"
                      :value="(item.fieldValueDetails || {})[f.id]"
                      :module-name="f.moduleName"
                      placeholder="---"
                      :field-reference-type="f.fieldReferenceType"
                    />
                  </template>
                  <template v-else>{{ getCustomFieldValue(f) }}</template>
                </FlotoFormItemReadOnly>
              </MCol>
            </MRow>
          </MCol>
        </MRow>
      </MCol>
    </MRow>
  </div>
</template>

<script>
import CurrencyPicker from '@components/data-picker/currency-picker'
import ProductPicker from '@components/data-picker/product-picker'
import VendorPicker from '@components/data-picker/vendor-picker'
import ManufacturerPicker from '@components/data-picker/manufacturer-picker'
import SoftwareLicenseTypePicker from '@components/data-picker/software-license-type-picker'
import AssetPickerDrawer from '@components/data-picker/asset-picker-drawer'
import { convertValueForDescription } from '@utils/value'
import { flattenRecursive } from '@data/recursive'
import CustomAsSystemPicker from '@modules/form/components/fields/custom-as-system.vue'
export default {
  name: 'PropertyComponentListItem',
  components: {
    ProductPicker,
    VendorPicker,
    ManufacturerPicker,
    SoftwareLicenseTypePicker,
    CurrencyPicker,
    AssetPickerDrawer,
    CustomAsSystemPicker,
  },
  inject: ['propertyComponentContext'],
  props: {
    moduleName: { type: String, required: true },
    item: { type: Object, required: true },
    type: { type: String, required: true },
    fieldDetails: { type: Object, required: true },
    customFields: {
      type: Array,
      default() {
        return []
      },
    },
    canEdit: { type: Boolean, default: false },
    canDelete: { type: Boolean, default: false },
    colSize: {
      type: Number,
      default() {
        return 3
      },
    },
    disabled: { type: Boolean, default: false },
  },
  methods: {
    getValue(field) {
      if (field.inputType === 'number') {
        return this.item[field.paramName]
          ? `${this.item[field.paramName]} ${
              field.unit ? this.$tc(field.unit) : ''
            }`
          : '---'
      }
      if (field.inputType === 'dropdown') {
        return this.item[field.paramName] && field.contextOptionKey
          ? (
              this.propertyComponentContext[field.contextOptionKey].find(
                (i) => i.key === this.item[field.paramName]
              ) || {}
            ).text
          : '---'
      }
      // if (field.inputType === 'dropdown') {
      //   return this.item[field.paramName]
      //     ? this.$tc(`asset_enum.${this.item[field.paramName]}`)
      //     : '---'
      // }
      const value = convertValueForDescription(
        field.inputType,
        this.item[field.paramName],
        field
      )
      if (value) {
        return value
      }
      return '---'
    },
    getCustomFieldValue(field) {
      if (field.inputType === 'dependent') {
        return this.getDependentFieldValue(field)
      }
      const value = convertValueForDescription(
        field.inputType,
        field.inputType === 'label'
          ? field.defaultValue
          : (this.item.fieldValueDetails || {})[field.id],
        field
      )
      if (value) {
        return value
      }
      return '---'
    },
    getDependentFieldValue(field) {
      const flattenedTree = flattenRecursive(field.tree)
      return (
        (
          flattenedTree.find(
            (o) => o.id === (this.item.fieldValueDetails || {})[field.id]
          ) || {}
        ).name || '---'
      )
    },
  },
}
</script>

<style lang="less" scoped>
.single-property-component-item {
  background-color: var(--common-widget-bg) !important;
  border-color: var(--border-color) !important;
  transition: box-shadow 0.2s linear;

  @apply flex flex-col w-full border-neutral-lighter border rounded border-solid p-2;

  &:hover {
    @apply shadow-lg;
  }
}
</style>
